// PDF Generator Service
// Generates RICS survey reports from DOCX templates

import PizZip from 'pizzip'
import Docxtemplater from 'docxtemplater'

/**
 * Generate a PDF report from survey data
 * @param {Object} survey - Survey data from the app
 * @param {Array} photos - Array of photo objects
 * @param {string} templateLevel - Survey level (1, 2, or 3)
 * @returns {Blob} PDF blob
 */
export async function generatePDFReport(survey, photos, templateLevel = '3') {
  try {
    // Load the appropriate template
    // Add version parameter to bust cache (v11 = Updated template with text placeholders instead of image elements)
    const templatePath = `/templates/docx/RICS_LEVEL_${templateLevel}_TEMPLATE.docx?v=11`
    const templateResponse = await fetch(templatePath)

    if (!templateResponse.ok) {
      throw new Error(`Template not found: ${templatePath}`)
    }

    const templateBuffer = await templateResponse.arrayBuffer()

    // Prepare survey data for template
    const templateData = await prepareSurveyData(survey, photos)

    // Generate DOCX from template
    const docxBlob = await fillTemplate(templateBuffer, templateData)

    // Convert DOCX to PDF (we'll use the existing PDF worker)
    const pdfBlob = await convertDocxToPdf(docxBlob)

    return pdfBlob

  } catch (error) {
    console.error('PDF generation failed:', error)
    throw error
  }
}

/**
 * Fill DOCX template with survey data
 */
async function fillTemplate(templateBuffer, data) {
  const zip = new PizZip(templateBuffer)

  // Configure docxtemplater without image module (free version doesn't work)
  const doc = new Docxtemplater(zip, {
    paragraphLoop: true,
    linebreaks: true,
    delimiters: { start: '{{', end: '}}' }
  })

  // Log sample data for debugging
  console.log('Template data sample:', {
    D1_RATING: data.D1_RATING,
    D1_RATING_ICON: data.D1_RATING_ICON,
    D1_PHOTO_1: data.D1_PHOTO_1,
    D1_PHOTO_1_NOTE: data.D1_PHOTO_1_NOTE
  })

  // Render template
  try {
    doc.render(data)
  } catch (error) {
    console.error('Template rendering error:', error)
    if (error.properties && error.properties.errors) {
      console.error('Detailed errors:', error.properties.errors)
    }
    throw error
  }

  const modifiedZip = doc.getZip()

  const docxBlob = modifiedZip.generate({
    type: 'blob',
    mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  })

  return docxBlob
}

/**
 * Prepare survey data in the format expected by the template
 */
async function prepareSurveyData(survey, photos) {
  const data = {
    // Basic property information (for legacy templates)
    propertyName: survey.propertyName || '',
    address: survey.address || '',
    postcode: survey.postcode || '',
    surveyorName: survey.surveyorName || '',
    surveyDate: formatDate(survey.createdAt),
    reportDate: formatDate(new Date()),
    surveyLevel: survey.surveyLevel || '3',

    // Cover Page / Property Details - ClystCore Template
    PROPERTY_NAME: survey.propertyName || '',
    ADDRESS: survey.address || '',
    POSTCODE: survey.postcode || '',
    PROPERTY_TYPE: survey.propertyType || '',
    CLIENT_NAME: survey.sectionKDeclaration?.clientName || '',
    INSPECTION_DATE: survey.sectionAInspection?.inspectionDate ? formatDate(survey.sectionAInspection.inspectionDate) : '',
    REPORT_REFERENCE: survey.sectionAInspection?.reportReference || '',
    SURVEYOR_RICS_NUMBER: survey.sectionAInspection?.surveyorRicsNumber || '',

    // Section A: About the Inspection - ClystCore Template
    SURVEYOR_NAME: survey.surveyorName || '',
    COMPANY_NAME: survey.sectionAInspection?.companyName || '',
    WEATHER_CONDITIONS: survey.sectionAInspection?.weatherConditions || '',
    PROPERTY_STATUS: survey.sectionAInspection?.propertyStatus || '',
    RELATED_PARTY_DISCLOSURE: survey.sectionAInspection?.relatedPartyDisclosure || '',
    PROPERTY_ADDRESS: `${survey.address || ''}, ${survey.postcode || ''}`,

    // Section A: About the Inspection - Level 2/3 Format {}
    weather: survey.sectionAInspection?.weatherConditions || '',
    propertyStatus: survey.sectionAInspection?.propertyStatus || '',
    relatedPartyDisclosure: survey.sectionAInspection?.relatedPartyDisclosure || '',
    inspectionDate: survey.sectionAInspection?.inspectionDate ? formatDate(survey.sectionAInspection.inspectionDate) : '',
    k_reportReference: survey.sectionAInspection?.reportReference || '',

    // Property details (legacy)
    propertyType: survey.propertyType || '',
    propertyAge: survey.propertyAge || '',
    bedrooms: survey.bedrooms || '',
    bathrooms: survey.bathrooms || '',
    floorAreaSqm: survey.floorAreaSqm || '',
    tenure: survey.tenure || '',
    listedStatus: survey.listedStatus || '',
    conservationArea: survey.conservationArea || '',
    parking: survey.parking || '',
    garden: survey.garden || '',

    // Section C: About the Property - ClystCore Template
    YEAR_BUILT: survey.sectionCProperty?.yearBuilt || survey.propertyAge || '',
    YEAR_EXTENDED: survey.sectionCProperty?.yearExtended || '',
    YEAR_CONVERTED: survey.sectionCProperty?.yearConverted || '',
    FLAT_INFO: survey.sectionCProperty?.flatInfo || '',
    CONSTRUCTION: survey.sectionCProperty?.construction || '',
    EPC_RATING: survey.sectionCProperty?.epcRating || '',
    MAINS_SERVICES: survey.sectionCProperty?.mainsServices || '',
    CENTRAL_HEATING: survey.sectionCProperty?.centralHeating || '',
    OTHER_SERVICES: survey.sectionCProperty?.otherServices || '',

    // Section C: Accommodation Grid - ClystCore Template
    ACCOM_LOWER_GROUND_LIVING: survey.sectionCProperty?.accomLowerGroundLiving || '',
    ACCOM_LOWER_GROUND_BEDROOMS: survey.sectionCProperty?.accomLowerGroundBedrooms || '',
    ACCOM_LOWER_GROUND_BATHROOMS: survey.sectionCProperty?.accomLowerGroundBathrooms || '',
    ACCOM_LOWER_GROUND_OTHER: survey.sectionCProperty?.accomLowerGroundOther || '',
    ACCOM_GROUND_LIVING: survey.sectionCProperty?.accomGroundLiving || '',
    ACCOM_GROUND_BEDROOMS: survey.sectionCProperty?.accomGroundBedrooms || '',
    ACCOM_GROUND_BATHROOMS: survey.sectionCProperty?.accomGroundBathrooms || '',
    ACCOM_GROUND_OTHER: survey.sectionCProperty?.accomGroundOther || '',
    ACCOM_FIRST_LIVING: survey.sectionCProperty?.accomFirstLiving || '',
    ACCOM_FIRST_BEDROOMS: survey.sectionCProperty?.accomFirstBedrooms || '',
    ACCOM_FIRST_BATHROOMS: survey.sectionCProperty?.accomFirstBathrooms || '',
    ACCOM_FIRST_OTHER: survey.sectionCProperty?.accomFirstOther || '',
    ACCOM_SECOND_LIVING: survey.sectionCProperty?.accomSecondLiving || '',
    ACCOM_SECOND_BEDROOMS: survey.sectionCProperty?.accomSecondBedrooms || '',
    ACCOM_SECOND_BATHROOMS: survey.sectionCProperty?.accomSecondBathrooms || '',
    ACCOM_SECOND_OTHER: survey.sectionCProperty?.accomSecondOther || '',
    ACCOM_THIRD_LIVING: survey.sectionCProperty?.accomThirdLiving || '',
    ACCOM_THIRD_BEDROOMS: survey.sectionCProperty?.accomThirdBedrooms || '',
    ACCOM_THIRD_BATHROOMS: survey.sectionCProperty?.accomThirdBathrooms || '',
    ACCOM_THIRD_OTHER: survey.sectionCProperty?.accomThirdOther || '',
    ACCOM_OTHER_LIVING: survey.sectionCProperty?.accomOtherLiving || '',
    ACCOM_OTHER_BEDROOMS: survey.sectionCProperty?.accomOtherBedrooms || '',
    ACCOM_OTHER_BATHROOMS: survey.sectionCProperty?.accomOtherBathrooms || '',
    ACCOM_OTHER_OTHER: survey.sectionCProperty?.accomOtherOther || '',

    // Section B: Overall Opinion
    overallOpinion: survey.sectionBOpinion || '',
    sectionBDocuments: survey.sectionBDocuments || '',
    sectionBFurtherInvestigations: survey.sectionBFurtherInvestigations || '',

    // Section C: Property Research
    propertyResearch: survey.propertyResearch || '',
    researchDate: survey.researchCompletedAt ? formatDate(survey.researchCompletedAt) : '',

    // Section H: Legal Issues - ClystCore Template
    H1_REGULATION: survey.sectionHLegal?.h1Regulation || '',
    H2_GUARANTEES: survey.sectionHLegal?.h2Guarantees || '',
    H3_OTHER_MATTERS: survey.sectionHLegal?.h3OtherMatters || '',
    // Legacy naming
    h1Regulation: survey.sectionHLegal?.h1Regulation || '',
    h2Guarantees: survey.sectionHLegal?.h2Guarantees || '',
    h3OtherMatters: survey.sectionHLegal?.h3OtherMatters || '',
    H3_OTHER_LEGAL: survey.sectionHLegal?.h3OtherMatters || '',

    // Section I: Risks - ClystCore Template
    I1_BUILDING_RISKS: survey.sectionIRisks?.i1Building || '',
    I2_GROUNDS_RISKS: survey.sectionIRisks?.i2Grounds || '',
    I3_PEOPLE_RISKS: survey.sectionIRisks?.i3People || '',
    I4_OTHER_RISKS: survey.sectionIRisks?.i4Other || '',
    // Legacy naming
    i1Building: survey.sectionIRisks?.i1Building || '',
    i2Grounds: survey.sectionIRisks?.i2Grounds || '',
    i3People: survey.sectionIRisks?.i3People || '',
    i4Other: survey.sectionIRisks?.i4Other || '',

    // Section J: Energy Matters (Level 3 only)
    j1Insulation: survey.sectionJEnergy?.j1Insulation || '',
    j2Heating: survey.sectionJEnergy?.j2Heating || '',
    j3Lighting: survey.sectionJEnergy?.j3Lighting || '',
    j4Ventilation: survey.sectionJEnergy?.j4Ventilation || '',
    j5General: survey.sectionJEnergy?.j5General || '',

    // Section K: Surveyor's Declaration - ClystCore Template
    PHONE_NUMBER: survey.sectionKDeclaration?.phone || '',
    EMAIL_ADDRESS: survey.sectionKDeclaration?.email || '',
    SURVEYOR_ADDRESS: survey.sectionKDeclaration?.address || '',
    QUALIFICATIONS: survey.sectionAInspection?.surveyorRicsNumber || survey.sectionKDeclaration?.qualifications || '',
    WEBSITE: survey.sectionKDeclaration?.website || '',
    REPORT_DATE: formatDate(new Date()),
    SIGNATURE: survey.sectionKDeclaration?.signature || '',
    SIGNATURE_DATE: formatDate(new Date()),
    // Legacy naming for Section K
    k_phone: survey.sectionKDeclaration?.phone || '',
    k_email: survey.sectionKDeclaration?.email || '',
    k_address: survey.sectionKDeclaration?.address || '',
    k_companyName: survey.sectionAInspection?.companyName || survey.sectionKDeclaration?.companyName || '',
    k_qualifications: survey.sectionAInspection?.surveyorRicsNumber || survey.sectionKDeclaration?.qualifications || '',
    k_website: survey.sectionKDeclaration?.website || '',
    k_clientName: survey.sectionKDeclaration?.clientName || '',
    k_reportDate: formatDate(new Date()),
    SURVEYOR_PHONE: survey.sectionKDeclaration?.phone || '',
    COMPANY_ADDRESS: survey.sectionKDeclaration?.address || '',
    SURVEYOR_QUALIFICATIONS: survey.sectionAInspection?.surveyorRicsNumber || survey.sectionKDeclaration?.qualifications || '',
    SURVEYOR_EMAIL: survey.sectionKDeclaration?.email || '',
    COMPANY_WEBSITE: survey.sectionKDeclaration?.website || ''
  }

  // Add category data (D1-G3)
  const categories = getAllCategoryCodes()

  for (const categoryCode of categories) {
    const categoryData = getCategoryData(survey, categoryCode)
    const categoryPhotos = photos.filter(p => p.categoryCode === categoryCode)

    // Add rating (ClystCore uses uppercase)
    data[`${categoryCode}_RATING`] = categoryData.rating || ''
    // Legacy lowercase
    data[`${categoryCode}_rating`] = categoryData.rating || ''

    // Add rating icon as Unicode circle (free image module doesn't work)
    const ratingValue = categoryData.rating || 'NI'
    data[`${categoryCode}_RATING_ICON`] = getRatingIconText(ratingValue)

    // Add conditional rating flags for colored circles
    data[`${categoryCode}_rating_1`] = categoryData.rating === '1'
    data[`${categoryCode}_rating_2`] = categoryData.rating === '2'
    data[`${categoryCode}_rating_3`] = categoryData.rating === '3'

    // Add comment (UPPERCASE for ClystCore Level 1)
    data[`${categoryCode}_COMMENTS`] = categoryData.comment || ''
    // Legacy lowercase for Level 2/3
    data[`${categoryCode}_comment`] = categoryData.comment || ''

    // Add individual photo placeholders for ClystCore template (up to 6 photos per element)
    // DOCX uses text placeholders since free image module doesn't work
    // PDF version will have actual embedded photos
    for (let i = 1; i <= 6; i++) {
      const photo = categoryPhotos[i - 1]
      if (photo) {
        data[`${categoryCode}_PHOTO_${i}`] = `[Photo ${i}]`
        data[`${categoryCode}_PHOTO_${i}_NOTE`] = photo.notes || ''
      } else {
        data[`${categoryCode}_PHOTO_${i}`] = ''
        data[`${categoryCode}_PHOTO_${i}_NOTE`] = ''
      }
    }
  }

  // Add section limitations (ClystCore uses uppercase)
  data.D_LIMITATIONS = survey.sectionDOutside?.limitations || survey.sectionDExternal?.limitations || ''
  data.E_LIMITATIONS = survey.sectionEInside?.limitations || survey.sectionEInternal?.limitations || ''
  data.F_LIMITATIONS = survey.sectionFServices?.limitations || ''
  data.G_LIMITATIONS = survey.sectionGGrounds?.limitations || ''
  // Legacy lowercase
  data.D_limitations = survey.sectionDOutside?.limitations || survey.sectionDExternal?.limitations || ''
  data.E_limitations = survey.sectionEInside?.limitations || survey.sectionEInternal?.limitations || ''
  data.F_limitations = survey.sectionFServices?.limitations || ''
  data.G_limitations = survey.sectionGGrounds?.limitations || ''
  data.LIMITATIONS = survey.sectionDOutside?.limitations || survey.sectionDExternal?.limitations || ''

  // Add rating tables for Section B
  const ratingTables = generateRatingTables(survey)
  data.rating3_items = ratingTables.rating3
  data.rating2_items = ratingTables.rating2
  data.rating1_items = ratingTables.rating1
  data.ratingNI_items = ratingTables.ratingNI

  // Add Level 1 format rating tables with numbered placeholders
  addLevel1RatingTables(data, ratingTables)

  return data
}

/**
 * Generate rating tables for Section B (Elements requiring urgent attention)
 */
function generateRatingTables(survey) {
  const rating3 = []
  const rating2 = []
  const rating1 = []
  const ratingNI = []

  const categoryNames = {
    'D1': 'Chimney stacks',
    'D2': 'Roof coverings',
    'D3': 'Rainwater pipes and gutters',
    'D4': 'Main walls',
    'D5': 'Windows',
    'D6': 'Outside doors (including patio doors)',
    'D7': 'Conservatory and porches',
    'D8': 'Other joinery and finishes',
    'D9': 'Other',
    'E1': 'Roof structure',
    'E2': 'Ceilings',
    'E3': 'Walls and partitions',
    'E4': 'Floors',
    'E5': 'Fireplaces, chimney breasts and flues',
    'E6': 'Built-in fittings (kitchen and other fittings)',
    'E7': 'Woodwork (e.g., staircase joinery)',
    'E8': 'Bathroom fittings',
    'E9': 'Other',
    'F1': 'Electricity',
    'F2': 'Gas/oil',
    'F3': 'Water',
    'F4': 'Heating',
    'F5': 'Water heating',
    'F6': 'Drainage',
    'F7': 'Common services (flats/maisonettes)',
    'G1': 'Garage',
    'G2': 'Permanent outbuildings and other structures',
    'G3': 'Other'
  }

  const sections = [
    { data: survey.sectionDOutside, codes: ['d1', 'd2', 'd3', 'd4', 'd5', 'd6', 'd7', 'd8', 'd9'] },
    { data: survey.sectionEInside, codes: ['e1', 'e2', 'e3', 'e4', 'e5', 'e6', 'e7', 'e8', 'e9'] },
    { data: survey.sectionFServices, codes: ['f1', 'f2', 'f3', 'f4', 'f5', 'f6', 'f7'] },
    { data: survey.sectionGGrounds, codes: ['g1', 'g2', 'g3'] }
  ]

  sections.forEach(section => {
    if (section.data) {
      section.codes.forEach(code => {
        const categoryData = section.data[code]
        if (categoryData && categoryData.rating) {
          const upperCode = code.toUpperCase()
          const item = {
            element_no: upperCode,
            element_name: categoryNames[upperCode] || upperCode
          }

          if (categoryData.rating === '3') rating3.push(item)
          else if (categoryData.rating === '2') rating2.push(item)
          else if (categoryData.rating === '1') rating1.push(item)
          else if (categoryData.rating === 'NI') ratingNI.push(item)
        }
      })
    }
  })

  return { rating3, rating2, rating1, ratingNI }
}

/**
 * Add Level 1 rating tables with numbered placeholders
 */
function addLevel1RatingTables(data, ratingTables) {
  // Urgent elements (Rating 3) - up to 7
  for (let i = 1; i <= 7; i++) {
    const item = ratingTables.rating3[i - 1]
    data[`URGENT_${i}_ELEMENT`] = item ? item.element_no : ''
    data[`URGENT_${i}_NAME`] = item ? item.element_name : ''
    data[`URGENT_${i}_COMMENTS`] = '' // Level 1 doesn't show comments in table
  }

  // Attention elements (Rating 2) - up to 5
  for (let i = 1; i <= 5; i++) {
    const item = ratingTables.rating2[i - 1]
    data[`ATTENTION_${i}_ELEMENT`] = item ? item.element_no : ''
    data[`ATTENTION_${i}_NAME`] = item ? item.element_name : ''
    data[`ATTENTION_${i}_COMMENTS`] = '' // Level 1 doesn't show comments in table
  }

  // No issues (Rating 1) - up to 3
  for (let i = 1; i <= 3; i++) {
    const item = ratingTables.rating1[i - 1]
    data[`NO_ISSUE_${i}_ELEMENT`] = item ? item.element_no : ''
    data[`NO_ISSUE_${i}_NAME`] = item ? item.element_name : ''
    data[`NO_ISSUE_${i}_COMMENTS`] = '' // Level 1 doesn't show comments in table
  }

  // Not inspected (Rating NI) - up to 3
  for (let i = 1; i <= 3; i++) {
    const item = ratingTables.ratingNI[i - 1]
    data[`NI_${i}_ELEMENT`] = item ? item.element_no : ''
    data[`NI_${i}_NAME`] = item ? item.element_name : ''
  }

  // Documents table - placeholder for now (will be populated when documents feature is added)
  for (let i = 1; i <= 7; i++) {
    data[`DOC_${i}_ELEMENT`] = ''
    data[`DOC_${i}_NAME`] = ''
    data[`DOC_${i}_RECEIVED`] = ''
  }
}

/**
 * Get all category codes
 */
function getAllCategoryCodes() {
  const categories = []

  // Section D
  for (let i = 1; i <= 9; i++) categories.push(`D${i}`)

  // Section E
  for (let i = 1; i <= 9; i++) categories.push(`E${i}`)

  // Section F
  for (let i = 1; i <= 7; i++) categories.push(`F${i}`)

  // Section G
  for (let i = 1; i <= 3; i++) categories.push(`G${i}`)

  return categories
}

/**
 * Get category data from survey
 */
function getCategoryData(survey, categoryCode) {
  // Determine which section this category belongs to
  const section = categoryCode[0] // D, E, F, or G

  let sectionData
  switch (section) {
    case 'D':
      sectionData = survey.sectionDExternal
      break
    case 'E':
      sectionData = survey.sectionEInternal
      break
    case 'F':
      sectionData = survey.sectionFServices
      break
    case 'G':
      sectionData = survey.sectionGGrounds
      break
    default:
      return { rating: '', comment: '' }
  }

  if (!sectionData || !sectionData[categoryCode]) {
    return { rating: '', comment: '' }
  }

  return {
    rating: sectionData[categoryCode].rating || '',
    comment: sectionData[categoryCode].comment || ''
  }
}

/**
 * Get photo as base64 for embedding in DOCX
 */
async function getPhotoAsBase64(photo) {
  try {
    // If photo has imageData (local), use it
    if (photo.imageData) {
      // imageData is already a data URL
      return photo.imageData.split(',')[1] // Remove "data:image/...;base64," prefix
    }

    // If photo is remote, fetch it
    if (photo.imageUrl) {
      const response = await fetch(photo.imageUrl)
      const blob = await response.blob()
      return await blobToBase64(blob)
    }

    return ''
  } catch (error) {
    console.error('Failed to get photo as base64:', error)
    return ''
  }
}

/**
 * Convert blob to base64
 */
function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      const base64 = reader.result.split(',')[1] // Remove data URL prefix
      resolve(base64)
    }
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

/**
 * Get rating icon as text with Unicode circle
 * @param {string} rating - Rating value: '1', '2', '3', or 'NI'
 * @returns {string} Unicode circle with rating number
 */
function getRatingIconText(rating) {
  // Map ratings to colored circles (will appear as text in DOCX)
  const icons = {
    '1': '● 1',  // Green circle - Word will need manual color formatting
    '2': '● 2',  // Amber circle
    '3': '● 3',  // Red circle
    'NI': '● NI' // Grey circle
  }

  return icons[rating] || icons['NI']
}

/**
 * Format date as DD/MM/YYYY
 */
function formatDate(date) {
  if (!date) return ''

  const d = new Date(date)
  const day = String(d.getDate()).padStart(2, '0')
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const year = d.getFullYear()

  return `${day}/${month}/${year}`
}

/**
 * Convert DOCX to PDF using our PDF worker
 */
async function convertDocxToPdf(docxBlob) {
  // For now, we'll just return the DOCX
  // In the future, you can integrate with LibreOffice or another conversion service
  console.warn('PDF conversion not yet implemented, returning DOCX')
  return docxBlob
}

export default {
  generatePDFReport
}
